package com.arena_web.ui.sections.news

import androidx.compose.runtime.*
import com.arena_web.data.dto.FootballMatchDto
import com.arena_web.data.dto.LineupEntryDto
import com.arena_web.data.dto.HeadToHeadDto
import com.arena_web.data.dto.MatchDetailDto
import com.arena_web.data.dto.StatEntryDto
import com.arena_web.ui.components.widgets.SkeletonBox
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.utils.mSize
import com.arena_web.ui.utils.mBorderRadius
import com.arena_web.ui.utils.s
import com.arena_web.ui.utils.sGap
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.A
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLScriptElement

// =============================================================================
// Match detail PAGE (/news/match/{id}) — full-bleed hero + floating back on
// phones, back row + centred column on desktop: the news-article detail's
// design language. Spec: docs/superpowers/specs/
// 2026-09-20-match-club-detail-design.md.
// =============================================================================

/** Carries the clicked match across SPA navigation so the detail page paints
 *  instantly; a hard reload falls back to the API using the URL context. */
object MatchNavSession {
    var match: FootballMatchDto? = null
}

/** Match context a detail page needs — from the nav session or the URL. */
data class MatchPageContext(
    val sport: String,
    val date: String,
    val competitionCode: String,
    val competitionName: String,
    val homeTeam: String,
    val awayTeam: String,
    val homeCrest: String,
    val awayCrest: String,
)

@Composable
fun MatchDetailPageContent(
    context: MatchPageContext,
    detail: MatchDetailDto?,
    h2h: HeadToHeadDto?,
    failed: Boolean,
    mobile: Boolean,
    onOpenPlayer: (String) -> Unit,
) {
    val t = LocalAppStrings.current
    val ctx = rememberPageContext()
    val heroUrl = detail?.banner ?: detail?.thumb

    Div(
        attrs = {
            style {
                property("width", "100%")
                property("flex-grow", "1")
                property("min-height", "0")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "none")
            }
        }
    ) {
        if (mobile) {
            // Full-bleed hero from the very top (page hides the global bar);
            // no image → a slim spacer so the floating back button has a home.
            Box(modifier = Modifier.fillMaxWidth().styleModifier { property("position", "relative") }) {
                if (heroUrl != null) {
                    Img(
                        src = heroUrl, alt = "",
                        attrs = Modifier.fillMaxWidth()
                            .styleModifier { property("height", "auto"); property("display", "block") }
                            .toAttrs()
                    )
                    Div(attrs = {
                        style {
                            property("position", "absolute")
                            property("inset", "0 0 auto 0")
                            property("height", "96px")
                            property("background", "linear-gradient(180deg, rgba(2,4,11,0.55) 0%, rgba(2,4,11,0) 100%)")
                            property("pointer-events", "none")
                        }
                    })
                } else {
                    Div(attrs = { style { property("width", "100%"); property("height", "64px") } })
                }
                FloatingBackButton { navigateBack(ctx) }
            }
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    if (!mobile) {
                        property("max-width", s(860))
                        property("margin", "0 auto")
                        property("padding-top", s(8))
                    } else {
                        property("padding", "0 16px")
                        // The score card overlaps the hero bottom.
                        if (heroUrl != null) property("margin-top", "-28px")
                        property("position", "relative")
                    }
                    property("gap", if (mobile) "16px" else s(18))
                }
        ) {
            if (!mobile) {
                DesktopBackRow { navigateBack(ctx) }
                if (heroUrl != null) {
                    Img(
                        src = heroUrl, alt = "",
                        attrs = Modifier.fillMaxWidth().styleModifier {
                            property("height", "auto")
                            property("display", "block")
                            property("border-radius", s(24))
                        }.toAttrs()
                    )
                }
            }

            ScoreCard(context, detail, mobile)

            if (failed && detail == null) {
                SpanText(
                    t.newsDetailUnavailable,
                    modifier = Modifier.fillMaxWidth().fontSize(if (mobile) 13.px else 14.px)
                        .styleModifier { property("text-align", "center"); property("color", "var(--arena-text-3)") }
                )
            }

            if (detail == null && !failed) {
                // Shimmer placeholders while the provider detail loads.
                SkeletonBox(height = 200.px, borderRadius = 18.px)
                SkeletonBox(height = 120.px, borderRadius = 18.px)
            }

            detail?.let { d ->
                var tab by remember(d.eventId) { mutableStateOf(0) }
                if (d.lineup.isNotEmpty()) {
                    DetailTabsRow(tab, listOf(t.newsMatchOverview, t.newsMatchLineups)) { tab = it }
                }
                if (tab == 0) {
                    d.video?.let { HighlightEmbed(it) }
                    if (d.timeline.isNotEmpty()) {
                        TimelineCard(d, context, mobile, onOpenPlayer)
                    }
                    if (d.stats.isNotEmpty()) {
                        StatsCard(d, context, mobile)
                    }
                    // Pre-match: nothing has happened yet — show both clubs'
                    // form and recent results instead of an empty page.
                    if (d.timeline.isEmpty() && d.stats.isEmpty()) {
                        TeamFormCard(d, context, mobile)
                    }
                    h2h?.let { HeadToHeadCard(it, context, mobile) }
                } else {
                    FormationPitch(d, mobile, onOpenPlayer)
                    LineupsCard(d, context, mobile, onOpenPlayer)
                }
            }
            Div(attrs = { style { property("width", "100%"); property("height", if (mobile) "96px" else "40px"); property("flex-shrink", "0") } })
        }
    }
}

// ── Score card ──

@Composable
private fun ScoreCard(
    context: MatchPageContext,
    detail: MatchDetailDto?,
    mobile: Boolean,
) {
    val ctx = rememberPageContext()
    val sportColors = LocalSportColors.current
    val status = detail?.status ?: ""
    val live = status == "IN_PLAY" || status == "PAUSED"
    val homeScore = detail?.homeScore
    val awayScore = detail?.awayScore

    Column(
        modifier = Modifier.fillMaxWidth()
            .styleModifier {
                property("border", "0.5px solid ${AppGradients.NewsCardBorder}")
                property("border-radius", if (mobile) "20px" else s(24))
                property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                property("padding", if (mobile) "18px 14px" else "${s(26)} ${s(24)}")
                property("gap", if (mobile) "10px" else s(12))
                property("box-shadow", "0 12px 32px rgba(2, 4, 11, 0.45)")
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Competition · venue meta line.
        val meta = listOfNotNull(
            context.competitionName.takeIf { it.isNotBlank() },
            detail?.venue,
        ).joinToString(" · ")
        if (meta.isNotBlank()) {
            SpanText(
                meta,
                modifier = Modifier.fontSize(if (mobile) 11.px else 13.px)
                    .styleModifier { property("color", "var(--arena-text-3)"); property("text-align", "center") }
            )
        }

        Row(
            modifier = Modifier.fillMaxWidth().styleModifier { property("gap", if (mobile) "8px" else s(16)) },
            verticalAlignment = Alignment.CenterVertically
        ) {
            ScoreTeamCell(
                context.homeTeam,
                detail?.homeCrest ?: context.homeCrest,
                detail?.homeTeamId,
                context.sport,
                mobile,
            )
            Column(
                modifier = Modifier.styleModifier { property("flex", "0 0 ${if (mobile) "96px" else s(150)}") },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (homeScore != null && awayScore != null) {
                    SpanText(
                        "$homeScore : $awayScore",
                        modifier = Modifier.fontSize(if (mobile) 30.px else 42.px).fontWeight(800)
                            .styleModifier {
                                property("color", if (live) "#FF4242" else "var(--arena-text-1)")
                                property("letter-spacing", "1px")
                                property("font-variant-numeric", "tabular-nums")
                            }
                    )
                } else {
                    SpanText(
                        kickoffTimeLabel(detail?.utcDate ?: "${context.date}T00:00:00Z"),
                        modifier = Modifier.fontSize(if (mobile) 24.px else 34.px).fontWeight(700)
                            .styleModifier { property("color", "var(--arena-text-1)") }
                    )
                }
                // Status chip.
                val chipLabel = when {
                    live -> "LIVE"
                    status == "FINISHED" -> "FT"
                    else -> kickoffDateLabel(detail?.utcDate ?: "${context.date}T00:00:00Z")
                }
                Div(attrs = {
                    style {
                        property("margin-top", "6px")
                        property("padding", "2px 10px")
                        property("border-radius", "100px")
                        property(
                            "background",
                            if (live) "rgba(255, 66, 66, 0.14)" else "rgba(60, 121, 241, 0.10)"
                        )
                        property("border", "0.5px solid ${if (live) "rgba(255,66,66,0.45)" else "rgba(180,204,246,0.18)"}")
                    }
                }) {
                    SpanText(
                        chipLabel,
                        modifier = Modifier.fontSize(if (mobile) 10.px else 12.px).fontWeight(700)
                            .styleModifier {
                                property("color", if (live) "#FF4242" else "var(--arena-text-2)")
                                property("letter-spacing", "0.6px")
                            }
                    )
                }
            }
            ScoreTeamCell(
                context.awayTeam,
                detail?.awayCrest ?: context.awayCrest,
                detail?.awayTeamId,
                context.sport,
                mobile,
            )
        }
    }
}

@Composable
private fun ScoreTeamCell(
    name: String,
    crest: String?,
    teamId: String?,
    sport: String,
    mobile: Boolean,
) {
    val ctx = rememberPageContext()
    Column(
        modifier = Modifier
            .styleModifier { property("flex", "1 1 0"); property("min-width", "0"); property("gap", "8px") }
            .then(
                if (teamId != null) {
                    Modifier.cursor(Cursor.Pointer).onClick { ctx.router.navigateTo("/news/club/$teamId?sport=$sport") }
                } else {
                    Modifier
                }
            ),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        if (!crest.isNullOrBlank()) {
            Img(
                src = crest, alt = "",
                attrs = Modifier.styleModifier {
                    property("width", if (mobile) "52px" else s(72))
                    property("height", if (mobile) "52px" else s(72))
                    property("object-fit", "contain")
                    property("filter", "drop-shadow(0 4px 10px rgba(2,4,11,0.5))")
                }.toAttrs()
            )
        }
        SpanText(
            name,
            modifier = Modifier.fontSize(if (mobile) 13.px else 16.px).fontWeight(600)
                .styleModifier {
                    property("color", "var(--arena-text-1)")
                    property("text-align", "center")
                    property("line-height", "1.25")
                }
        )
    }
}

// ── Tabs ──

@Composable
fun DetailTabsRow(
    active: Int,
    labels: List<String>,
    onChange: (Int) -> Unit,
) {
    val sportColors = LocalSportColors.current
    Row(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "22px"); property("justify-content", "center") }) {
        labels.forEachIndexed { index, label ->
            SpanText(
                label,
                modifier = Modifier.fontSize(14.px).fontWeight(600).cursor(Cursor.Pointer)
                    .onClick { onChange(index) }
                    .styleModifier {
                        property("color", if (index == active) sportColors.primary.toString() else "var(--arena-text-3)")
                        property("border-bottom", if (index == active) "2px solid ${sportColors.primary}" else "2px solid transparent")
                        property("padding-bottom", "5px")
                        property("transition", "color 0.15s ease")
                    }
            )
        }
    }
}

// ── Highlight ──
//
// Some rights holders (LaLiga notably) block playback of their highlight
// videos inside third-party embeds — the iframe then shows YouTube's raw
// "Видео недоступно" screen. The IFrame Player API reports that as error
// 101/150, so the player is created through the API and swapped for a
// thumbnail card linking out to YouTube when playback is blocked.

@Composable
fun HighlightEmbed(videoUrl: String) {
    val videoId = youtubeVideoId(videoUrl) ?: return
    var blocked by remember(videoId) { mutableStateOf(false) }

    if (blocked) {
        HighlightWatchOnYoutubeCard(videoId, videoUrl)
        return
    }

    Div(attrs = {
        style {
            property("width", "100%")
            property("aspect-ratio", "16 / 9")
            property("border-radius", "16px")
            property("overflow", "hidden")
            property("flex-shrink", "0")
            property("border", "0.5px solid ${AppGradients.NewsCardBorder}")
        }
        ref { container ->
            var disposed = false
            var player: dynamic = null
            val holder = document.createElement("div") as HTMLElement
            holder.style.width = "100%"
            holder.style.height = "100%"
            container.appendChild(holder)
            ensureYouTubeIframeApi(
                onReady = onReady@{
                    if (disposed) return@onReady
                    val opts = jsObject()
                    opts.videoId = videoId
                    opts.width = "100%"
                    opts.height = "100%"
                    val playerVars = jsObject()
                    playerVars.rel = 0
                    playerVars.playsinline = 1
                    playerVars.origin = window.location.origin
                    opts.playerVars = playerVars
                    val events = jsObject()
                    events.onError = { e: dynamic ->
                        // 100 = removed/private, 101/150 = embedding disallowed.
                        val code = ((e?.data as? Number)?.toInt()) ?: -1
                        if (code == 100 || code == 101 || code == 150) blocked = true
                    }
                    opts.events = events
                    player = jsNew(window.asDynamic().YT.Player, holder, opts)
                },
                onFailure = onFailure@{
                    // API script unavailable (ad-block etc.) — bare embed as before.
                    if (disposed) return@onFailure
                    holder.innerHTML =
                        "<iframe src=\"https://www.youtube.com/embed/$videoId\" width=\"100%\" height=\"100%\" " +
                            "frameborder=\"0\" allowfullscreen allow=\"accelerometer; autoplay; clipboard-write; " +
                            "encrypted-media; gyroscope; picture-in-picture\"></iframe>"
                },
            )
            onDispose {
                disposed = true
                try {
                    player?.destroy()
                } catch (_: Throwable) {
                }
                container.innerHTML = ""
            }
        }
    })
}

@Composable
private fun HighlightWatchOnYoutubeCard(videoId: String, videoUrl: String) {
    val t = LocalAppStrings.current
    A(href = videoUrl, attrs = {
        attr("target", "_blank")
        attr("rel", "noopener")
        style {
            property("display", "block")
            property("position", "relative")
            property("width", "100%")
            property("aspect-ratio", "16 / 9")
            property("border-radius", "16px")
            property("overflow", "hidden")
            property("flex-shrink", "0")
            property("border", "0.5px solid ${AppGradients.NewsCardBorder}")
            property("text-decoration", "none")
        }
    }) {
        Img(src = "https://i.ytimg.com/vi/$videoId/hqdefault.jpg", alt = "", attrs = {
            style {
                property("width", "100%")
                property("height", "100%")
                property("object-fit", "cover")
                property("display", "block")
                property("filter", "brightness(0.5)")
            }
        })
        Div(attrs = {
            style {
                property("position", "absolute")
                property("inset", "0")
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", "10px")
            }
        }) {
            Div(attrs = {
                style {
                    property("width", "56px")
                    property("height", "40px")
                    property("border-radius", "12px")
                    property("background", "rgba(255, 0, 0, 0.9)")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                }
            }) {
                Div(attrs = {
                    style {
                        property("width", "0")
                        property("height", "0")
                        property("border-style", "solid")
                        property("border-width", "8px 0 8px 14px")
                        property("border-color", "transparent transparent transparent #FFFFFF")
                    }
                })
            }
            SpanText(
                t.newsWatchOnYoutube,
                modifier = Modifier.fontSize(14.px).styleModifier {
                    property("color", "#FFFFFF")
                    property("font-weight", "600")
                }
            )
        }
    }
}

private fun youtubeVideoId(url: String): String? {
    val id = when {
        url.contains("watch?v=") -> url.substringAfter("watch?v=").substringBefore("&")
        url.contains("youtu.be/") -> url.substringAfter("youtu.be/").substringBefore("?")
        else -> null
    }
    return id?.takeIf { it.matches(Regex("[A-Za-z0-9_-]{6,20}")) }
}

// The IFrame API script is loaded once; every embed queues its callback until
// YouTube fires the global onYouTubeIframeAPIReady hook.
private var ytApiRequested = false
private val ytApiReadyCallbacks = mutableListOf<() -> Unit>()
private val ytApiFailureCallbacks = mutableListOf<() -> Unit>()

private fun ensureYouTubeIframeApi(onReady: () -> Unit, onFailure: () -> Unit) {
    val yt = window.asDynamic().YT
    if (yt != null && yt.Player != null) {
        onReady()
        return
    }
    ytApiReadyCallbacks.add(onReady)
    ytApiFailureCallbacks.add(onFailure)
    if (ytApiRequested) return
    ytApiRequested = true
    window.asDynamic().onYouTubeIframeAPIReady = {
        ytApiFailureCallbacks.clear()
        val ready = ytApiReadyCallbacks.toList()
        ytApiReadyCallbacks.clear()
        ready.forEach { it() }
    }
    val script = document.createElement("script") as HTMLScriptElement
    script.src = "https://www.youtube.com/iframe_api"
    script.addEventListener("error", {
        ytApiRequested = false
        val failed = ytApiFailureCallbacks.toList()
        ytApiFailureCallbacks.clear()
        ytApiReadyCallbacks.clear()
        failed.forEach { it() }
    })
    document.head?.appendChild(script)
}

private fun jsObject(): dynamic = js("({})")

@Suppress("UNUSED_PARAMETER")
private fun jsNew(ctor: dynamic, arg0: dynamic, arg1: dynamic): dynamic = js("new ctor(arg0, arg1)")

// ── Timeline: centred minute axis, home left / away right ──

@Composable
private fun TimelineCard(
    d: MatchDetailDto,
    context: MatchPageContext,
    mobile: Boolean,
    onOpenPlayer: (String) -> Unit,
) {
    SectionCard {
        Column(modifier = Modifier.fillMaxWidth().styleModifier { property("padding", if (mobile) "6px 0" else "10px 0") }) {
            d.timeline.sortedBy { it.minute ?: 0 }.forEach { entry ->
                Row(
                    modifier = Modifier.fillMaxWidth()
                        .styleModifier { property("padding", if (mobile) "6px 12px" else "7px 18px"); property("gap", "10px") },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Home half — right-aligned toward the axis.
                    Row(
                        modifier = Modifier.styleModifier {
                            property("flex", "1 1 0")
                            property("min-width", "0")
                            property("overflow", "hidden")
                            property("justify-content", "flex-end")
                            property("gap", "8px")
                        },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        if (entry.home) TimelineEvent(entry, mobile, alignEnd = true, onOpenPlayer)
                    }
                    // Minute pill on the axis.
                    Div(attrs = {
                        style {
                            property("flex", "0 0 ${if (mobile) "40px" else "48px"}")
                            property("text-align", "center")
                            property("padding", "2px 0")
                            property("border-radius", "100px")
                            property("background", "rgba(60, 121, 241, 0.10)")
                            property("border", "0.5px solid rgba(180, 204, 246, 0.14)")
                        }
                    }) {
                        SpanText(
                            entry.minute?.let { "$it′" } ?: "–",
                            modifier = Modifier.fontSize(if (mobile) 10.px else 11.px).fontWeight(600)
                                .styleModifier { property("color", "var(--arena-text-2)") }
                        )
                    }
                    // Away half — left-aligned from the axis.
                    Row(
                        modifier = Modifier.styleModifier {
                            property("flex", "1 1 0")
                            property("min-width", "0")
                            property("overflow", "hidden")
                            property("justify-content", "flex-start")
                            property("gap", "8px")
                        },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        if (!entry.home) TimelineEvent(entry, mobile, alignEnd = false, onOpenPlayer)
                    }
                }
            }
        }
    }
}

@Composable
private fun TimelineEvent(
    entry: com.arena_web.data.dto.TimelineEntryDto,
    mobile: Boolean,
    alignEnd: Boolean,
    onOpenPlayer: (String) -> Unit,
) {
    val glyph = timelineGlyph(entry.type, entry.detail)
    val nameBlock: @Composable () -> Unit = {
        Column(
            modifier = Modifier.styleModifier {
                property("min-width", "0")
                property("overflow", "hidden")
                property("flex", "1 1 auto")
                property("gap", "0")
            },
            horizontalAlignment = if (alignEnd) Alignment.End else Alignment.Start
        ) {
            SpanText(entry.player, modifier = timelinePlayerModifier(mobile, if (alignEnd) "end" else "start"))
            // Assist under the scorer — the football convention.
            entry.assist?.let { assist ->
                SpanText(
                    assist,
                    modifier = Modifier.fontSize(if (mobile) 9.px else 10.px)
                        .styleModifier {
                            property("color", "var(--arena-text-3)")
                            property("display", "block")
                            property("width", "100%")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                            property("text-align", if (alignEnd) "end" else "start")
                        }
                )
            }
        }
    }
    val avatar: @Composable () -> Unit = {
        Box(
            modifier = entry.playerId?.let { id ->
                Modifier.cursor(Cursor.Pointer).onClick { onOpenPlayer(id) }
            } ?: Modifier
        ) { TimelinePlayerAvatar(entry.photo, mobile) }
    }
    if (alignEnd) {
        nameBlock()
        SpanText(glyph, modifier = Modifier.fontSize(if (mobile) 12.px else 13.px))
        avatar()
    } else {
        avatar()
        SpanText(glyph, modifier = Modifier.fontSize(if (mobile) 12.px else 13.px))
        nameBlock()
    }
}

@Composable
private fun TimelinePlayerAvatar(photo: String?, mobile: Boolean) {
    val size = if (mobile) "26px" else "30px"
    if (!photo.isNullOrBlank()) {
        Img(
            src = photo, alt = "",
            attrs = Modifier.styleModifier {
                property("width", size)
                property("height", size)
                property("border-radius", "100px")
                property("object-fit", "cover")
                property("object-position", "top")
                property("background", "radial-gradient(circle at 50% 30%, rgba(60,121,241,0.25), rgba(60,121,241,0.06))")
                property("border", "0.5px solid rgba(180, 204, 246, 0.2)")
                property("flex-shrink", "0")
            }.toAttrs()
        )
    }
}

private fun timelinePlayerModifier(mobile: Boolean, align: String): Modifier =
    Modifier.fontSize(if (mobile) 12.px else 13.px)
        .styleModifier {
            property("color", "var(--arena-text-1)")
            property("display", "block")
            property("width", "100%")
            property("overflow", "hidden")
            property("text-overflow", "ellipsis")
            property("white-space", "nowrap")
            property("text-align", align)
        }

private fun timelineGlyph(type: String, detail: String?): String =
    when (type.lowercase()) {
        "goal" -> "⚽"
        "card" -> if (detail?.contains("red", ignoreCase = true) == true) "🟥" else "🟨"
        "subst" -> "🔁"
        "var" -> "VAR"
        else -> "•"
    }

// ── Stats: mirrored bars from the centre ──

@Composable
private fun StatsCard(
    d: MatchDetailDto,
    context: MatchPageContext,
    mobile: Boolean,
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val stats = d.stats
    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "10px") }) {
        SpanText(
            t.newsMatchStats,
            modifier = Modifier.fontSize(if (mobile) 14.px else 16.px).fontWeight(700)
                .styleModifier { property("color", "var(--arena-text-1)") }
        )
        SectionCard {
            Column(modifier = Modifier.fillMaxWidth().styleModifier { property("padding", if (mobile) "10px 14px" else "14px 20px"); property("gap", if (mobile) "12px" else "14px") }) {
                // Crest header — who is which side of the bars.
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    listOf(d.homeCrest ?: context.homeCrest, d.awayCrest ?: context.awayCrest).forEach { crest ->
                        if (crest.isNotBlank()) {
                            Img(
                                src = crest, alt = "",
                                attrs = Modifier.styleModifier {
                                    property("width", if (mobile) "24px" else "28px")
                                    property("height", if (mobile) "24px" else "28px")
                                    property("object-fit", "contain")
                                }.toAttrs()
                            )
                        } else {
                            Div(attrs = { style { property("width", "24px") } })
                        }
                    }
                }
                stats.forEach { stat ->
                    val home = stat.home.toDoubleOrNull() ?: 0.0
                    val away = stat.away.toDoubleOrNull() ?: 0.0
                    val total = (home + away).takeIf { it > 0 } ?: 1.0
                    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "4px") }) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            SpanText(
                                stat.home,
                                modifier = Modifier.fontSize(if (mobile) 12.px else 13.px).fontWeight(if (home >= away) 700 else 400)
                                    .styleModifier { property("color", "var(--arena-text-1)"); property("font-variant-numeric", "tabular-nums") }
                            )
                            SpanText(
                                stat.name,
                                modifier = Modifier.fontSize(if (mobile) 11.px else 12.px)
                                    .styleModifier { property("color", "var(--arena-text-3)"); property("text-align", "center") }
                            )
                            SpanText(
                                stat.away,
                                modifier = Modifier.fontSize(if (mobile) 12.px else 13.px).fontWeight(if (away > home) 700 else 400)
                                    .styleModifier { property("color", "var(--arena-text-1)"); property("font-variant-numeric", "tabular-nums") }
                            )
                        }
                        // Mirrored bars meeting at the centre.
                        Row(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "3px") }) {
                            Div(attrs = {
                                style {
                                    property("flex", "1 1 0")
                                    property("height", "4px")
                                    property("border-radius", "100px")
                                    property("background", "rgba(180, 204, 246, 0.10)")
                                    property("position", "relative")
                                    property("overflow", "hidden")
                                }
                            }) {
                                Div(attrs = {
                                    style {
                                        property("position", "absolute")
                                        property("right", "0")
                                        property("top", "0")
                                        property("bottom", "0")
                                        property("width", "${(home / total * 100).toInt()}%")
                                        property("border-radius", "100px")
                                        property("background", if (home >= away) sportColors.primary.toString() else "rgba(180, 204, 246, 0.35)")
                                    }
                                })
                            }
                            Div(attrs = {
                                style {
                                    property("flex", "1 1 0")
                                    property("height", "4px")
                                    property("border-radius", "100px")
                                    property("background", "rgba(180, 204, 246, 0.10)")
                                    property("position", "relative")
                                    property("overflow", "hidden")
                                }
                            }) {
                                Div(attrs = {
                                    style {
                                        property("position", "absolute")
                                        property("left", "0")
                                        property("top", "0")
                                        property("bottom", "0")
                                        property("width", "${(away / total * 100).toInt()}%")
                                        property("border-radius", "100px")
                                        property("background", if (away > home) sportColors.primary.toString() else "rgba(180, 204, 246, 0.35)")
                                    }
                                })
                            }
                        }
                    }
                }
            }
        }
    }
}

// ── Formation pitch ──

/** Dark stylized pitch: home rows GK→FWD top-down, away mirrored below.
 *  Rows derive from the positions (no formation string at the provider);
 *  hidden when either side has too few starters to draw honestly. */
@Composable
private fun FormationPitch(
    d: MatchDetailDto,
    mobile: Boolean,
    onOpenPlayer: (String) -> Unit,
) {
    val homeStarters = d.lineup.filter { it.home && !it.substitute }
    val awayStarters = d.lineup.filter { !it.home && !it.substitute }
    if (homeStarters.size < MIN_PITCH_PLAYERS || awayStarters.size < MIN_PITCH_PLAYERS) return

    fun rows(entries: List<LineupEntryDto>): List<List<LineupEntryDto>> =
        (0..3).map { row ->
            entries.filter { positionRow(it.position) == row }.sortedBy { sideOrder(it.position) }
        }.filter { it.isNotEmpty() }

    Div(attrs = {
        style {
            property("position", "relative")
            property("width", "100%")
            property("border-radius", "18px")
            property("overflow", "hidden")
            property("border", "0.5px solid ${AppGradients.NewsCardBorder}")
            property(
                "background",
                "linear-gradient(180deg, var(--l-s3, #081228) 0%, var(--l-s3, #0A1730) 48%, var(--l-s3, #0A1730) 52%, var(--l-s3, #081228) 100%)"
            )
            property("padding", if (mobile) "14px 6px" else "22px 12px")
        }
    }) {
        // Markings: halfway line, centre circle, two goal boxes.
        listOf(
            "top:50%;left:0;right:0;height:1px;background:rgba(180,204,246,0.10)",
            "top:50%;left:50%;width:72px;height:72px;margin:-36px 0 0 -36px;border:1px solid rgba(180,204,246,0.10);border-radius:100px",
            "top:-1px;left:50%;width:34%;height:34px;margin-left:-17%;border:1px solid rgba(180,204,246,0.10);border-top:none",
            "bottom:-1px;left:50%;width:34%;height:34px;margin-left:-17%;border:1px solid rgba(180,204,246,0.10);border-bottom:none",
        ).forEach { css ->
            Div(attrs = { attr("style", "position:absolute;pointer-events:none;$css") })
        }

        Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", if (mobile) "12px" else "18px") }) {
            rows(homeStarters).forEach { row -> PitchRow(row, mobile, onOpenPlayer) }
            Div(attrs = { style { property("height", if (mobile) "8px" else "12px") } })
            rows(awayStarters).reversed().forEach { row -> PitchRow(row, mobile, onOpenPlayer) }
        }
    }
}

@Composable
private fun PitchRow(
    players: List<LineupEntryDto>,
    mobile: Boolean,
    onOpenPlayer: (String) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().styleModifier { property("justify-content", "space-evenly") },
        verticalAlignment = Alignment.CenterVertically
    ) {
        players.forEach { player ->
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier
                    .styleModifier { property("gap", "3px"); property("min-width", "0") }
                    .then(
                        player.playerId?.let { id -> Modifier.cursor(Cursor.Pointer).onClick { onOpenPlayer(id) } }
                            ?: Modifier
                    )
            ) {
                Box(modifier = Modifier.styleModifier { property("position", "relative") }) {
                    if (!player.photo.isNullOrBlank()) {
                        Img(
                            src = player.photo, alt = "",
                            attrs = Modifier.styleModifier {
                                property("width", if (mobile) "34px" else "44px")
                                property("height", if (mobile) "34px" else "44px")
                                property("border-radius", "100px")
                                property("object-fit", "cover")
                                property("object-position", "top")
                                property("background", "radial-gradient(circle at 50% 30%, rgba(60,121,241,0.3), rgba(60,121,241,0.08))")
                                property("border", "1px solid rgba(180, 204, 246, 0.25)")
                                property("display", "block")
                            }.toAttrs()
                        )
                    } else {
                        Div(attrs = {
                            style {
                                property("width", if (mobile) "34px" else "44px")
                                property("height", if (mobile) "34px" else "44px")
                                property("border-radius", "100px")
                                property("background", "rgba(60, 121, 241, 0.14)")
                                property("border", "1px solid rgba(180, 204, 246, 0.25)")
                            }
                        })
                    }
                    player.number?.let { number ->
                        Div(attrs = {
                            style {
                                property("position", "absolute")
                                property("right", "-5px")
                                property("bottom", "-3px")
                                property("min-width", "14px")
                                property("padding", "0 3px")
                                property("border-radius", "100px")
                                property("background", "var(--l-s3, #0B1B3E)")
                                property("border", "0.5px solid rgba(180, 204, 246, 0.35)")
                                property("text-align", "center")
                            }
                        }) {
                            SpanText(
                                number,
                                modifier = Modifier.fontSize(8.px).fontWeight(700)
                                    .styleModifier { property("color", "var(--arena-text-2)") }
                            )
                        }
                    }
                }
                SpanText(
                    player.player.substringAfterLast(" "),
                    modifier = Modifier.fontSize(if (mobile) 9.px else 10.px).fontWeight(500)
                        .styleModifier {
                            property("color", "var(--arena-text-2)")
                            property("max-width", if (mobile) "56px" else "76px")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                            property("text-align", "center")
                        }
                )
            }
        }
    }
}

private const val MIN_PITCH_PLAYERS = 7

private fun positionRow(position: String): Int =
    when {
        position.contains("Goalkeeper", ignoreCase = true) -> 0
        position.contains("Back", ignoreCase = true) -> 1
        position.contains("Midfield", ignoreCase = true) -> 2
        else -> 3
    }

private fun sideOrder(position: String): Int =
    when {
        position.contains("Left", ignoreCase = true) -> 0
        position.contains("Right", ignoreCase = true) -> 2
        else -> 1
    }

// ── Pre-match team form ──

@Composable
private fun TeamFormCard(
    d: MatchDetailDto,
    context: MatchPageContext,
    mobile: Boolean,
) {
    val ctx = rememberPageContext()
    val t = LocalAppStrings.current
    val sides = listOf(
        Triple(d.homeTeam.ifBlank { context.homeTeam }, d.homeCrest ?: context.homeCrest, d.homeRecent),
        Triple(d.awayTeam.ifBlank { context.awayTeam }, d.awayCrest ?: context.awayCrest, d.awayRecent),
    ).filter { it.third.isNotEmpty() }
    if (sides.isEmpty()) return

    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "10px") }) {
        SpanText(
            t.newsClubLastMatches,
            modifier = Modifier.fontSize(if (mobile) 14.px else 16.px).fontWeight(700)
                .styleModifier { property("color", "var(--arena-text-1)") }
        )
        sides.forEach { (teamName, crest, recent) ->
            SectionCard {
                Column(modifier = Modifier.fillMaxWidth().styleModifier { property("padding", if (mobile) "12px 14px" else "14px 20px"); property("gap", "8px") }) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Row(modifier = Modifier.styleModifier { property("gap", "8px"); property("min-width", "0") }, verticalAlignment = Alignment.CenterVertically) {
                            if (crest.isNotBlank()) {
                                Img(
                                    src = crest, alt = "",
                                    attrs = Modifier.styleModifier {
                                        property("width", "22px"); property("height", "22px")
                                        property("object-fit", "contain"); property("flex-shrink", "0")
                                    }.toAttrs()
                                )
                            }
                            SpanText(
                                teamName,
                                modifier = Modifier.fontSize(if (mobile) 13.px else 14.px).fontWeight(700)
                                    .styleModifier {
                                        property("color", "var(--arena-text-1)")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                        property("white-space", "nowrap")
                                    }
                            )
                        }
                        // Form dots, newest right.
                        Row(modifier = Modifier.styleModifier { property("gap", "4px"); property("flex-shrink", "0") }) {
                            recent.mapNotNull { matchFormOf(it, teamName) }.take(5).reversed().forEach { r ->
                                Div(attrs = {
                                    style {
                                        property("width", "18px")
                                        property("height", "18px")
                                        property("border-radius", "100px")
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                        property(
                                            "background",
                                            when (r) {
                                                'W' -> "rgba(34, 197, 94, 0.18)"
                                                'L' -> "rgba(239, 68, 68, 0.18)"
                                                else -> "rgba(148, 163, 184, 0.15)"
                                            }
                                        )
                                    }
                                }) {
                                    SpanText(
                                        r.toString(),
                                        modifier = Modifier.fontSize(9.px).fontWeight(700)
                                            .styleModifier {
                                                property(
                                                    "color",
                                                    when (r) {
                                                        'W' -> "#22C55E"
                                                        'L' -> "#EF4444"
                                                        else -> "#94A3B8"
                                                    }
                                                )
                                            }
                                    )
                                }
                            }
                        }
                    }
                    recent.take(4).forEachIndexed { index, match ->
                        if (index > 0) DetailRowDivider()
                        Row(
                            modifier = Modifier.fillMaxWidth().cursor(Cursor.Pointer)
                                .onClick { openMatchPage(ctx, context.sport, match) }
                                .styleModifier { property("padding", "6px 0"); property("gap", "10px") },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            SpanText(
                                kickoffDateLabel(match.utcDate),
                                modifier = Modifier.fontSize(10.px)
                                    .styleModifier { property("flex", "0 0 40px"); property("color", "var(--arena-text-3)") }
                            )
                            // Team crests + names — mirrors the club-detail
                            // recent-matches rows (MiniCrest layout there).
                            Row(
                                modifier = Modifier.styleModifier {
                                    property("flex", "1 1 0")
                                    property("min-width", "0")
                                    property("gap", "5px")
                                },
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                RecentRowCrest(match.homeCrest)
                                RecentRowTeamName(match.homeTeam, mobile)
                                SpanText(
                                    "–",
                                    modifier = Modifier.fontSize(if (mobile) 11.px else 12.px)
                                        .styleModifier { property("color", "var(--arena-text-3)"); property("flex-shrink", "0") }
                                )
                                RecentRowCrest(match.awayCrest)
                                RecentRowTeamName(match.awayTeam, mobile)
                            }
                            SpanText(
                                if (match.homeScore != null && match.awayScore != null) {
                                    "${match.homeScore}:${match.awayScore}"
                                } else {
                                    kickoffTimeLabel(match.utcDate)
                                },
                                modifier = Modifier.fontSize(if (mobile) 12.px else 13.px).fontWeight(700)
                                    .styleModifier {
                                        property("flex-shrink", "0")
                                        property("color", "var(--arena-text-1)")
                                        property("font-variant-numeric", "tabular-nums")
                                    }
                            )
                        }
                    }
                }
            }
        }
    }
}

/** 16px team crest in a recent-matches row (blank URL renders nothing). */
@Composable
private fun RecentRowCrest(url: String) {
    if (url.isNotBlank()) {
        Img(
            src = url, alt = "",
            attrs = Modifier.styleModifier {
                property("width", "16px")
                property("height", "16px")
                property("object-fit", "contain")
                property("flex-shrink", "0")
            }.toAttrs()
        )
    }
}

@Composable
private fun RecentRowTeamName(name: String, mobile: Boolean) {
    SpanText(
        name,
        modifier = Modifier.fontSize(if (mobile) 12.px else 13.px)
            .styleModifier {
                property("min-width", "0")
                property("color", "var(--arena-text-1)")
                property("overflow", "hidden")
                property("text-overflow", "ellipsis")
                property("white-space", "nowrap")
            }
    )
}

/** W/D/L of one finished match from [clubName]'s perspective. */
private fun matchFormOf(match: FootballMatchDto, clubName: String): Char? {
    val home = match.homeScore ?: return null
    val away = match.awayScore ?: return null
    val isHome = sameSideTeam(match.homeTeam, clubName)
    val my = if (isHome) home else away
    val their = if (isHome) away else home
    return when {
        my > their -> 'W'
        my < their -> 'L'
        else -> 'D'
    }
}

// ── Head-to-head ──

@Composable
private fun HeadToHeadCard(
    h2h: HeadToHeadDto,
    context: MatchPageContext,
    mobile: Boolean,
) {
    val t = LocalAppStrings.current
    val ctx = rememberPageContext()
    val sportColors = LocalSportColors.current
    val total = (h2h.homeWins + h2h.draws + h2h.awayWins).takeIf { it > 0 } ?: return
    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "10px") }) {
        SpanText(
            t.newsMatchH2h,
            modifier = Modifier.fontSize(if (mobile) 14.px else 16.px).fontWeight(700)
                .styleModifier { property("color", "var(--arena-text-1)") }
        )
        SectionCard {
            Column(modifier = Modifier.fillMaxWidth().styleModifier { property("padding", if (mobile) "12px 14px" else "16px 20px"); property("gap", "10px") }) {
                // Wins summary: home count · draws · away count.
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    listOf(
                        Triple(h2h.homeWins, h2h.homeName, sportColors.primary.toString()),
                        Triple(h2h.draws, "=", "rgba(148, 163, 184, 0.9)"),
                        Triple(h2h.awayWins, h2h.awayName, "rgba(180, 204, 246, 0.9)"),
                    ).forEachIndexed { index, (count, label, color) ->
                        Column(
                            horizontalAlignment = when (index) {
                                0 -> Alignment.Start
                                2 -> Alignment.End
                                else -> Alignment.CenterHorizontally
                            },
                            modifier = Modifier.styleModifier { property("min-width", "0"); property("gap", "1px") }
                        ) {
                            SpanText(
                                count.toString(),
                                modifier = Modifier.fontSize(if (mobile) 18.px else 22.px).fontWeight(800)
                                    .styleModifier { property("color", color) }
                            )
                            SpanText(
                                label,
                                modifier = Modifier.fontSize(if (mobile) 10.px else 11.px)
                                    .styleModifier {
                                        property("color", "var(--arena-text-3)")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                        property("white-space", "nowrap")
                                        property("max-width", if (mobile) "110px" else "200px")
                                    }
                            )
                        }
                    }
                }
                // Proportional strip.
                Row(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "2px") }) {
                    listOf(
                        h2h.homeWins to sportColors.primary.toString(),
                        h2h.draws to "rgba(148, 163, 184, 0.45)",
                        h2h.awayWins to "rgba(180, 204, 246, 0.55)",
                    ).forEach { (count, color) ->
                        if (count > 0) {
                            Div(attrs = {
                                style {
                                    property("flex", "${count.toDouble() / total} 1 0")
                                    property("height", "5px")
                                    property("border-radius", "100px")
                                    property("background", color)
                                }
                            })
                        }
                    }
                }
                // Past meetings — tap opens that match's page.
                h2h.matches.take(5).forEachIndexed { index, match ->
                    if (index > 0) DetailRowDivider()
                    Row(
                        modifier = Modifier.fillMaxWidth().cursor(Cursor.Pointer)
                            .onClick { openMatchPage(ctx, context.sport, match) }
                            .styleModifier { property("padding", "7px 0"); property("gap", "10px") },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            kickoffDateLabel(match.utcDate),
                            modifier = Modifier.fontSize(10.px)
                                .styleModifier { property("flex", "0 0 40px"); property("color", "var(--arena-text-3)") }
                        )
                        SpanText(
                            "${match.homeTeam} — ${match.awayTeam}",
                            modifier = Modifier.fontSize(if (mobile) 12.px else 13.px)
                                .styleModifier {
                                    property("flex", "1 1 0")
                                    property("min-width", "0")
                                    property("color", "var(--arena-text-1)")
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                    property("white-space", "nowrap")
                                }
                        )
                        SpanText(
                            if (match.homeScore != null && match.awayScore != null) {
                                "${match.homeScore}:${match.awayScore}"
                            } else {
                                kickoffTimeLabel(match.utcDate)
                            },
                            modifier = Modifier.fontSize(if (mobile) 12.px else 13.px).fontWeight(700)
                                .styleModifier {
                                    property("flex-shrink", "0")
                                    property("color", "var(--arena-text-1)")
                                    property("font-variant-numeric", "tabular-nums")
                                }
                        )
                    }
                }
            }
        }
    }
}

// ── Lineups ──

@Composable
private fun LineupsCard(
    d: MatchDetailDto,
    context: MatchPageContext,
    mobile: Boolean,
    onOpenPlayer: (String) -> Unit,
) {
    val t = LocalAppStrings.current
    SectionCard {
        Row(
            modifier = Modifier.fillMaxWidth()
                .styleModifier { property("padding", if (mobile) "14px 12px" else "18px 20px"); property("gap", if (mobile) "14px" else "24px") }
        ) {
            listOf(
                Triple(d.homeTeam.ifBlank { context.homeTeam }, d.homeCrest ?: context.homeCrest, d.lineup.filter { it.home }),
                Triple(d.awayTeam.ifBlank { context.awayTeam }, d.awayCrest ?: context.awayCrest, d.lineup.filterNot { it.home }),
            ).forEach { (teamName, crest, entries) ->
                Column(modifier = Modifier.styleModifier { property("flex", "1 1 0"); property("min-width", "0"); property("gap", "8px") }) {
                    Row(modifier = Modifier.styleModifier { property("gap", "8px") }, verticalAlignment = Alignment.CenterVertically) {
                        if (crest.isNotBlank()) {
                            Img(
                                src = crest, alt = "",
                                attrs = Modifier.styleModifier {
                                    property("width", "20px"); property("height", "20px"); property("object-fit", "contain")
                                }.toAttrs()
                            )
                        }
                        SpanText(
                            teamName,
                            modifier = Modifier.fontSize(if (mobile) 12.px else 14.px).fontWeight(700)
                                .styleModifier {
                                    property("color", "var(--arena-text-1)")
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                    property("white-space", "nowrap")
                                }
                        )
                    }
                    val (starters, subs) = entries.partition { !it.substitute }
                    starters.forEach { LineupPlayerRow(it, mobile, onOpenPlayer) }
                    if (subs.isNotEmpty()) {
                        SpanText(
                            t.newsMatchSubstitutes.uppercase(),
                            modifier = Modifier.fontSize(10.px).fontWeight(700)
                                .styleModifier {
                                    property("color", "var(--arena-text-3)")
                                    property("letter-spacing", "0.8px")
                                    property("margin-top", "8px")
                                }
                        )
                        subs.forEach { LineupPlayerRow(it, mobile, onOpenPlayer) }
                    }
                }
            }
        }
    }
}

@Composable
private fun LineupPlayerRow(
    entry: LineupEntryDto,
    mobile: Boolean,
    onOpenPlayer: (String) -> Unit,
) {
    val avatarSize = if (mobile) "30px" else "36px"
    Row(
        modifier = Modifier.fillMaxWidth()
            .then(
                entry.playerId?.let { id -> Modifier.cursor(Cursor.Pointer).onClick { onOpenPlayer(id) } } ?: Modifier
            )
            .styleModifier { property("gap", if (mobile) "8px" else "10px"); property("padding", "3px 0") },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(modifier = Modifier.styleModifier { property("position", "relative"); property("flex-shrink", "0") }) {
            if (!entry.photo.isNullOrBlank()) {
                Img(
                    src = entry.photo, alt = "",
                    attrs = Modifier.styleModifier {
                        property("width", avatarSize)
                        property("height", avatarSize)
                        property("border-radius", "100px")
                        property("object-fit", "cover")
                        property("object-position", "top")
                        property("background", "radial-gradient(circle at 50% 30%, rgba(60,121,241,0.25), rgba(60,121,241,0.06))")
                        property("border", "0.5px solid rgba(180, 204, 246, 0.2)")
                        property("display", "block")
                    }.toAttrs()
                )
            } else {
                Div(attrs = {
                    style {
                        property("width", avatarSize)
                        property("height", avatarSize)
                        property("border-radius", "100px")
                        property("background", "rgba(60, 121, 241, 0.10)")
                        property("border", "0.5px solid rgba(180, 204, 246, 0.16)")
                    }
                })
            }
            // Squad number badge on the avatar's corner.
            entry.number?.let { number ->
                Div(attrs = {
                    style {
                        property("position", "absolute")
                        property("right", "-4px")
                        property("bottom", "-2px")
                        property("min-width", "15px")
                        property("padding", "0 3px")
                        property("border-radius", "100px")
                        property("background", "var(--l-s3, #0B1B3E)")
                        property("border", "0.5px solid rgba(180, 204, 246, 0.3)")
                        property("text-align", "center")
                    }
                }) {
                    SpanText(
                        number,
                        modifier = Modifier.fontSize(8.px).fontWeight(700)
                            .styleModifier { property("color", "var(--arena-text-2)") }
                    )
                }
            }
        }
        Column(modifier = Modifier.styleModifier { property("min-width", "0"); property("gap", "0") }) {
            SpanText(
                entry.player,
                modifier = Modifier.fontSize(if (mobile) 12.px else 13.px).fontWeight(500)
                    .styleModifier {
                        property("color", "var(--arena-text-1)")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    }
            )
            if (entry.position.isNotBlank()) {
                SpanText(
                    entry.position,
                    modifier = Modifier.fontSize(if (mobile) 9.px else 10.px)
                        .styleModifier { property("color", "var(--arena-text-3)") }
                )
            }
        }
    }
}

// ── Shared page chrome (also used by the club page) ──

@Composable
fun FloatingBackButton(onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .mSize(36)
            .mBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "absolute")
                property("top", "calc(env(safe-area-inset-top, 0px) + 12px)")
                property("left", "16px")
                property("background", "var(--l-glass-base, rgba(2, 10, 28, 0.55))")
                property("backdrop-filter", "blur(12px)")
                property("-webkit-backdrop-filter", "blur(12px)")
                property("border", "0.3px solid rgba(180, 204, 246, 0.25)")
                property("z-index", "5")
            },
        contentAlignment = Alignment.Center
    ) {
        com.arena_web.ui.components.icons.Icon(
            src = com.arena_web.ui.theme.AppIcons.ChevronBack,
            modifier = Modifier.mSize(16).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) }
        )
    }
}

@Composable
fun DesktopBackRow(onClick: () -> Unit) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .sGap(8)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier { property("width", "fit-content") },
        verticalAlignment = Alignment.CenterVertically
    ) {
        com.arena_web.ui.components.icons.Icon(
            src = com.arena_web.ui.theme.AppIcons.ChevronForward,
            modifier = Modifier.styleModifier { property("transform", "rotate(180deg)") }
        )
        SpanText(
            t.commonBack,
            modifier = Modifier.fontSize(15.px).styleModifier { property("color", "var(--arena-text-2)") }
        )
    }
}

fun navigateBack(ctx: com.varabyte.kobweb.core.PageContext) {
    if (kotlinx.browser.window.history.length > 1) kotlinx.browser.window.history.back()
    else ctx.router.navigateTo("/news")
}

@Composable
fun SectionCard(content: @Composable () -> Unit) {
    Column(
        modifier = Modifier.fillMaxWidth()
            .styleModifier {
                property("border", "0.5px solid ${AppGradients.NewsCardBorder}")
                property("border-radius", "18px")
                property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                property("overflow", "hidden")
            }
    ) { content() }
}

@Composable
fun DetailRowDivider() {
    Div(attrs = {
        style {
            property("width", "100%")
            property("height", "1px")
            property("background", "rgba(180, 204, 246, 0.08)")
        }
    })
}

/** Case/punctuation-blind team equality with containment — mirrors the
 *  backend resolver so timeline/lineup sides match across providers. */
fun sameSideTeam(a: String, b: String): Boolean {
    val x = a.lowercase().filter { it.isLetter() }
    val y = b.lowercase().filter { it.isLetter() }
    return x.isNotEmpty() && y.isNotEmpty() && (x == y || x.contains(y) || y.contains(x))
}

/** "15:30" in the viewer's local time. */
fun kickoffTimeLabel(utcDate: String): String {
    if (utcDate.isBlank()) return ""
    val d = kotlin.js.Date(utcDate)
    val h = d.getHours().toString().padStart(2, '0')
    val m = d.getMinutes().toString().padStart(2, '0')
    return "$h:$m"
}

/** "20.09" in the viewer's local time. */
fun kickoffDateLabel(utcDate: String): String {
    if (utcDate.isBlank()) return ""
    val d = kotlin.js.Date(utcDate)
    val day = d.getDate().toString().padStart(2, '0')
    val month = (d.getMonth() + 1).toString().padStart(2, '0')
    return "$day.$month"
}
